// The leaf module, not "@/lib/openapi": these predicates need no HTTP client,
// and going through the barrel pulled the whole generated SDK into every
// consumer of this file.
import {
    isHttpExceptionModel,
    isHttpValidationError,
} from "@/lib/openapi/errors";

// Retry configuration for transient sync failures.
export const MAX_SYNC_RETRIES = 2;
export const SYNC_RETRY_DELAYS = [1000, 3000];

/** classifySyncError's own `errorType` for a rejected payload — the server
 *  answered, it just refused this body. Not one of the domain ErrorTypes. */
export const REJECTED_ERROR_TYPE = "REJECTED";

/** The `msg` lines of a validation-shaped rejection, in the server's words. */
const rejectionMessages = (error: unknown): string[] => {
    if (!isHttpValidationError(error)) return [];
    return (error.detail ?? [])
        .map((entry) => entry?.msg)
        .filter(
            (msg): msg is string => typeof msg === "string" && msg.trim() !== ""
        );
};

/**
 * Whether an error is transient and worth retrying.
 * Domain errors (CONFLICT, NOT_FOUND, etc.) are not retryable — they require user action.
 */
export const isSyncRetryable = (error: unknown): boolean => {
    // A validation-shaped error (`detail[].loc`) is the server rejecting THIS
    // payload: a retry re-sends the identical body for the identical answer.
    //
    // Tested ahead of the domain-error check because an endpoint that builds
    // its detail by hand carries no `type` at all, so `isHttpExceptionModel`
    // reads it as "not a server error" and retries. That is how
    // update_business_entity reports a duplicate object name — which is the
    // likeliest reason a rename ever fails, and used to burn two retries
    // (1s + 3s) before reporting itself as "Network error".
    if (isHttpValidationError(error)) return false;
    if (!isHttpExceptionModel(error)) return true;
    const type = String(error.detail[0]?.type);
    return type === "INTERNAL" || type === "CONNECTION_ERROR";
};

export const sleep = (ms: number) =>
    new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Classify a sync error into a user-facing message.
 *
 * `errorType` is the domain error's type (e.g. "CONFLICT") when present, so
 * callers can branch — notably the thread stale-base soft-block, which is a
 * recoverable CONFLICT that should be caught up rather than "refresh the page".
 */
export const classifySyncError = (
    error: unknown
): { details: string; isDomainError: boolean; errorType?: string } => {
    // A rejection of this payload, in the server's own words — "Another entity
    // with this name already exists in this domain." beats both "Network
    // error" (what a hand-built detail used to fall through to) and
    // "Server error: undefined" (what a real 422 produced, since a
    // ValidationError carries `msg`, not `message`).
    const rejection = rejectionMessages(error);
    if (rejection.length > 0) {
        return {
            details: rejection.join(" "),
            isDomainError: true,
            errorType: REJECTED_ERROR_TYPE,
        };
    }

    const isDomainError = isHttpExceptionModel(error);
    let details: string;
    let errorType: string | undefined;

    if (isDomainError) {
        errorType = String(error.detail[0].type);
        if (errorType === "CONFLICT") {
            // Prefer the server's own message — the thread stale-base soft-block
            // ("Main changed this since the thread started — catch up / resolve
            // to edit") is far clearer than a generic line. Fall back only when
            // the server provided no message.
            const serverMessage = error.detail[0].message;
            details =
                typeof serverMessage === "string" && serverMessage.trim()
                    ? serverMessage
                    : "Local changes conflict with the server";
        } else if (errorType === "NOT_FOUND") {
            details =
                "The entity you are trying to sync does not exist anymore";
        } else {
            details = `Server error: ${error.detail[0].message}`;
        }
    } else {
        details = "Network error — could not reach the server";
    }

    return { details, isDomainError, errorType };
};
